<template>
  <modal title="Create Album" ref="modal">
    <h2 class="text-subtitle1 q-mt-none justify-be">Date Range</h2>
    <select-date-range v-model="dateRange" />
    <q-separator class="q-mt-md" />
    <h2 class="text-subtitle1">Social Sites</h2>
    <select-site
      v-model="socialSiteIds"
      @loaded="$refs.socialSites.selectAll()"
      ref="socialSites"
    />

    <q-separator class="q-my-md" />
    <q-input label="Album Name" v-model="name" outlined />

    <div class="q-mt-md text-center">
      <total-posts :filters="postFilters" v-slot="{ isLoading, total }">
        This album will contain <strong v-show="!isLoading">{{ total }}</strong>
      </total-posts>
    </div>

    <template v-slot:actions>
      <async-btn
        label="Create"
        color="primary"
        :task="task"
        :disabled="!canCreate"
        @click="create"
      />
    </template>
  </modal>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { ModalMixin } from '@/mixins';
import {
  Report,
  OpenDateRange,
  PostFilters,
  Modal as ModalElement,
} from '@/type';
import Modal from '@/components/modal/Modal.vue';
import AsyncBtn from '@/components/AsyncBtn.vue';
import SelectSite from '@/social/site/SelectSite.vue';
import { allStatuses as allPostStatuses } from '@/social/post/post';
import notify from '@/components/notify';
import SelectDateRange from './SelectDateRange.vue';
import TotalPosts from '../generate/TotalPosts.vue';
import { createAlbum } from '.';

interface Data {
  startDate?: string|null,
  endDate?: string|null,
  socialSiteIds: number[],
  name: string,
  task: Promise<Report>|null
}

export default defineComponent({
  emits: ['ok'],
  mixins: [ModalMixin],
  components: {
    Modal,
    SelectDateRange,
    SelectSite,
    TotalPosts,
    AsyncBtn,
  },
  props: {
    clientId: {
      type: Number,
      required: true,
    },
  },
  data(): Data {
    return {
      startDate: null,
      endDate: null,
      socialSiteIds: [],
      name: '',
      task: null,
    };
  },
  computed: {
    dateRange: {
      get(): OpenDateRange {
        return {
          start: this.startDate,
          end: this.endDate,
        };
      },
      set(dateRange: OpenDateRange) {
        this.startDate = dateRange.start;
        this.endDate = dateRange.end;
      },
    },
    postFilters(): PostFilters {
      return {
        clientIds: [this.clientId],
        since: this.startDate,
        until: this.endDate,
        statuses: allPostStatuses(),
        socialSiteIds: this.socialSiteIds,
        isTrashed: false,
        isPrivate: false,
      };
    },
    canCreate(): boolean {
      return (this.name !== '');
    },
  },
  methods: {
    create() {
      this.task = createAlbum(
        this.clientId, this.name, this.postFilters.statuses,
        this.socialSiteIds, [], this.startDate, this.endDate,
      );
      this.task.then((report: Report) => {
        this.$emit('ok', report);
        (this.$refs.modal as ModalElement).hide();
        notify('Album created.');
      });
    },
  },
});
</script>
